/**
 * React Query hooks for fetching rack configurations
 * @see GitLab #995 — Multiple Rack Instances
 */

import { useQuery } from '@tanstack/react-query';

import { useNetworkStatus } from './useOfflineRequestsQuery';
import { groupRacksByStatus } from '../../components/racks/rackListUtils';
import { api } from '../../services/api';
import {
  applyPendingRackOperations,
  getCachedRackList,
  getInFlightRackOpsForRequest,
  getOfflineRackById,
  getOfflineRackGrouped,
  getOfflineRackList,
  mirrorRacksToOfflineRequestCache,
} from '../../services/rackOfflineSync';

import type {
  RackConfiguration,
  RackConfigurationsResponse,
  GroupedRackConfigurations,
} from '../../types/rack';

/**
 * Query keys for consistent cache management
 */
export const rackConfigurationsKeys = {
  all: ['rackConfigurations'] as const,
  lists: () => [...rackConfigurationsKeys.all, 'list'] as const,
  list: (requestId: string) =>
    [...rackConfigurationsKeys.lists(), requestId] as const,
  grouped: (requestId: string) =>
    [...rackConfigurationsKeys.all, 'grouped', requestId] as const,
  details: () => [...rackConfigurationsKeys.all, 'detail'] as const,
  detail: (requestId: string, rackId: number) =>
    [...rackConfigurationsKeys.details(), requestId, rackId] as const,
};

/**
 * Hook for fetching all rack configurations for a request
 * Includes offline fallback to return cached rack configurations
 */
export const useRackConfigurations = (requestId: string, enabled = true) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: rackConfigurationsKeys.list(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineRackList(requestId);
      }

      const response = await api.get<RackConfigurationsResponse>(
        `/requests/${requestId}/racks`,
      );
      await mirrorRacksToOfflineRequestCache(requestId, response.racks);
      // Overlay un-synced offline ops on the server list so a create still in
      // the queue (e.g. the reconnect refetch beat the sync) is not dropped.
      const pendingOps = await getInFlightRackOpsForRequest(requestId);
      return applyPendingRackOperations(requestId, response.racks, pendingOps);
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    meta: {
      errorMessage: 'Failed to fetch rack configurations.',
    },
  });
};

/**
 * Hook for fetching rack configurations grouped by status (existing vs new)
 * Includes offline fallback (derived from cached rack list)
 */
export const useRackConfigurationsGrouped = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: rackConfigurationsKeys.grouped(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineRackGrouped(requestId);
      }

      const response = await api.get<GroupedRackConfigurations>(
        `/requests/${requestId}/racks/grouped`,
      );
      const allRacks = [...response.existing, ...response.new];
      await mirrorRacksToOfflineRequestCache(requestId, allRacks);
      // Overlay un-synced offline ops before regrouping (see flat query note).
      const pendingOps = await getInFlightRackOpsForRequest(requestId);
      return groupRacksByStatus(
        applyPendingRackOperations(requestId, allRacks, pendingOps),
      );
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch grouped rack configurations.',
    },
  });
};

/**
 * Hook for fetching a single rack configuration
 * Includes offline fallback from cached request racks
 */
export const useRackConfiguration = (
  requestId: string,
  rackId: number,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: rackConfigurationsKeys.detail(requestId, rackId),
    queryFn: async () => {
      if (!isOnline) {
        const cached = await getOfflineRackById(requestId, rackId);
        if (!cached) {
          throw new Error('Rack configuration not found in offline cache');
        }
        return cached;
      }

      const response = await api.get<RackConfiguration>(
        `/requests/${requestId}/racks/${rackId}`,
      );
      const list = await getCachedRackList(requestId);
      const merged = list.some(r => r.id === response.id)
        ? list.map(r => (r.id === response.id ? response : r))
        : [...list, response];
      await mirrorRacksToOfflineRequestCache(requestId, merged);
      return response;
    },
    enabled: !!requestId && !!rackId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch rack configuration details.',
    },
  });
};
